import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Loader2, Save, Phone, Mail, MapPin, Building2, RotateCcw } from "lucide-react";

/**
 * Phase 18.13 — Company / contact settings.
 *
 * Owner/Admin can edit; Manager + Content Manager see a view-only form.
 * Backend (auth.py P_SETTINGS_EDIT) enforces RBAC — the disabled inputs
 * here are a UX hint only.
 */
const FIELDS = [
  // [api_key, label, type, group]
  ["company_name", "Bedrijfsnaam", "text", "company"],
  ["website_url", "Website URL", "url", "company"],
  ["address_line", "Adresregel", "text", "address"],
  ["postal_code", "Postcode", "text", "address"],
  ["city", "Plaats", "text", "address"],
  ["country", "Land", "text", "address"],
  ["phone", "Telefoon (E.164)", "tel", "phone"],
  ["phone_display", "Telefoon (weergave)", "text", "phone"],
  ["whatsapp", "WhatsApp (E.164)", "tel", "phone"],
  ["whatsapp_display", "WhatsApp (weergave)", "text", "phone"],
  ["email", "Algemeen e-mail", "email", "email"],
  ["finance_email", "Finance e-mail", "email", "email"],
  ["vat_number", "BTW-nummer", "text", "legal"],
  ["eori_number", "EORI-nummer", "text", "legal"],
  ["kvk_number", "KVK-nummer", "text", "legal"],
];

const GROUP_META = {
  company: { title: "Bedrijf", icon: Building2 },
  address: { title: "Adres", icon: MapPin },
  phone: { title: "Telefoon & WhatsApp", icon: Phone },
  email: { title: "E-mailadressen", icon: Mail },
  legal: { title: "KVK / BTW / EORI", icon: Building2 },
};

function emptySettings() {
  return Object.fromEntries(FIELDS.map(([k]) => [k, ""]));
}

export default function CompanyContactSection() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin"].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(emptySettings());
  const [form, setForm] = useState(emptySettings());
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/company-contact");
      const s = r.data?.settings || {};
      const merged = { ...emptySettings(), ...s };
      setOriginal(merged);
      setForm(merged);
      setMeta({
        updated_at: r.data?.updated_at || null,
        updated_by: r.data?.updated_by || null,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isDirty = FIELDS.some(([k]) => (form[k] || "") !== (original[k] || ""));

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const onReset = () => setForm(original);

  const onSave = async () => {
    // Only send changed fields — backend treats unset fields as "leave as-is".
    const diff = {};
    for (const [k] of FIELDS) {
      if ((form[k] || "") !== (original[k] || "")) {
        diff[k] = form[k] || "";
      }
    }
    if (!Object.keys(diff).length) {
      toast.info("Geen wijzigingen om op te slaan.");
      return;
    }
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/company-contact", diff);
      const merged = { ...emptySettings(), ...(r.data?.settings || {}) };
      setOriginal(merged);
      setForm(merged);
      toast.success("Bedrijfsgegevens bijgewerkt — footer en contactpagina volgen automatisch.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-8 flex items-center gap-3 text-[#0A192F]" data-testid="company-contact-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Bedrijfsgegevens laden…</span>
      </div>
    );
  }

  const grouped = FIELDS.reduce((acc, f) => {
    (acc[f[3]] = acc[f[3]] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="company-contact-section">
      <div className="bg-white border border-gray-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Bedrijfsgegevens</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Centrale contactgegevens voor de hele website (footer, contactpagina, e-mailtemplates).
              {canEdit ? " Wijzigingen worden direct opgeslagen en gebruikt." : " (Alleen-lezen — vraag een Owner/Admin om wijzigingen.)"}
            </p>
          </div>
          {meta.updated_at && (
            <div className="text-[11px] text-gray-500 text-right">
              <div>Laatst bijgewerkt</div>
              <div className="font-chivo font-bold text-[#0A192F]">
                {new Date(meta.updated_at).toLocaleString("nl-NL")}
              </div>
              {meta.updated_by && <div>door {meta.updated_by}</div>}
            </div>
          )}
        </div>
      </div>

      {Object.entries(grouped).map(([groupKey, items]) => {
        const meta = GROUP_META[groupKey];
        const Icon = meta?.icon || Building2;
        return (
          <div key={groupKey} className="bg-white border border-gray-200 p-5" data-testid={`company-contact-group-${groupKey}`}>
            <h3 className="flex items-center gap-2 font-chivo font-bold text-sm uppercase tracking-wide text-[#0A192F] mb-4">
              <Icon className="w-4 h-4 text-[#1a5193]" /> {meta?.title || groupKey}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map(([key, label, type]) => (
                <div key={key} className={items.length === 1 ? "sm:col-span-2" : ""}>
                  <Label htmlFor={`cc-${key}`} className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1.5 block">
                    {label}
                  </Label>
                  <Input
                    id={`cc-${key}`}
                    type={type}
                    value={form[key] || ""}
                    onChange={onChange(key)}
                    disabled={!canEdit || saving}
                    data-testid={`company-contact-input-${key}`}
                    className="rounded-none border-gray-300"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {canEdit && (
        <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {isDirty ? <span className="font-semibold text-amber-700">Onopgeslagen wijzigingen.</span> : "Geen wijzigingen."}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onReset}
              disabled={!isDirty || saving}
              data-testid="company-contact-reset"
              className="rounded-none"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button
              onClick={onSave}
              disabled={!isDirty || saving}
              data-testid="company-contact-save"
              className="rounded-none bg-[#082c50] hover:bg-[#0a3463] text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Opslaan
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
